import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { SERVICES, DURATIONS } from "@/lib/site-data";
import BranchChooser from "./BranchChooser";

export default function Pricing() {
    const [waOpen, setWaOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const openBooking = (svc, dur, price) => {
        setSelected({ svc, dur, price });
        setWaOpen(true);
    };

    const message = selected
        ? `Hi, I would like to book a ${selected.svc} (${selected.dur}, R${selected.price}) at Aekkarag Thai Massage. Could you share availability?`
        : "Hi, I would like to book a Thai massage.";

    return (
        <section
            id="pricing"
            data-testid="pricing-section"
            className="relative py-24 lg:py-36 bg-cream grain"
        >
            <div className="max-w-6xl mx-auto px-5 lg:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14 lg:mb-20">
                    <span className="eyebrow inline-flex items-center gap-3">
                        <span className="w-8 h-px bg-gold" />
                        Price List
                        <span className="w-8 h-px bg-gold" />
                    </span>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-maroon tracking-tight leading-[1.05]">
                        Honest pricing.{" "}
                        <em className="font-light text-gold-dark">No surprises.</em>
                    </h2>
                    <p className="mt-5 text-base lg:text-lg text-ink-soft font-light">
                        All treatments available in 30, 45, 60 and 90-minute sessions. Walk-ins are
                        always welcome.
                    </p>
                </div>

                {/* Desktop pricing table */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="hidden md:block border border-gold/30 rounded-sm bg-beige/30"
                >
                    <div className="grid grid-cols-[2fr_repeat(4,1fr)] bg-maroon text-cream px-8 py-5 text-xs tracking-[0.22em] uppercase rounded-t-sm">
                        <div className="text-gold-light">Treatment</div>
                        {DURATIONS.map((d) => (
                            <div key={d} className="text-center text-gold-light">
                                {d}
                            </div>
                        ))}
                    </div>
                    {SERVICES.map((s, idx) => (
                        <div
                            key={s.id}
                            className={`grid grid-cols-[2fr_repeat(4,1fr)] px-8 py-5 items-center transition-colors hover:bg-beige/70 ${
                                idx !== SERVICES.length - 1 ? "border-b border-gold/15" : ""
                            }`}
                            data-testid={`pricing-row-${s.id}`}
                        >
                            <div>
                                <div className="font-serif text-xl lg:text-2xl text-maroon">
                                    {s.name}
                                </div>
                            </div>
                            {s.prices.map((p, i) => (
                                <button
                                    key={i}
                                    onClick={() => openBooking(s.name, DURATIONS[i], p)}
                                    data-testid={`pricing-cell-${s.id}-${i}`}
                                    className="text-center font-serif text-2xl lg:text-3xl text-ink hover:text-gold-dark transition-colors group"
                                >
                                    <span className="text-sm align-top text-ink-muted mr-0.5">R</span>
                                    {p}
                                    <div className="h-px w-6 mx-auto mt-1 bg-gold/0 group-hover:bg-gold transition-all" />
                                </button>
                            ))}
                        </div>
                    ))}
                </motion.div>

                {/* Mobile pricing list */}
                <div className="md:hidden space-y-4">
                    {SERVICES.map((s) => (
                        <div
                            key={s.id}
                            className="border border-gold/30 bg-beige/40 p-5"
                            data-testid={`pricing-mobile-${s.id}`}
                        >
                            <h4 className="font-serif text-xl text-maroon mb-3">{s.name}</h4>
                            <div className="grid grid-cols-4 gap-2">
                                {DURATIONS.map((d, i) => (
                                    <button
                                        key={i}
                                        onClick={() => openBooking(s.name, d, s.prices[i])}
                                        className="flex flex-col items-center py-2 border border-gold/20 hover:border-gold transition-all"
                                    >
                                        <span className="text-[0.6rem] tracking-widest uppercase text-ink-muted">
                                            {d.replace(" min", "m")}
                                        </span>
                                        <span className="font-serif text-lg text-maroon">
                                            R{s.prices[i]}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 flex items-center justify-center gap-3 text-sm text-ink-soft">
                    <CheckCircle2 size={16} className="text-gold-dark" />
                    <span className="tracking-wide">
                        Walk-ins welcome · Tap any price to book that session on WhatsApp
                    </span>
                </div>
            </div>

            <BranchChooser
                open={waOpen}
                onOpenChange={setWaOpen}
                title={selected ? `Book ${selected.svc}` : "Book your treatment"}
                description={
                    selected
                        ? `${selected.dur} · R${selected.price} — choose a branch to confirm on WhatsApp.`
                        : "Choose a branch to confirm on WhatsApp."
                }
                message={message}
            />
        </section>
    );
}
